import type {ReactElement} from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {trackGA4NumberOfSectionsInEmailCampaign} from '@Libraries/email-campaigns-library';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerExportOption} from '@EmailMakerReact/email-maker-react.types';
import {copyEmailHtmlAsTextForExport, copyEmailHtmlForExport, downloadHtmlForMarketingEmail, recordEmailExport} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {getCurrentIdEmailCampaign} from '@EmailMakerReact/lib/email-maker-storage-library';
import {authenticateUser} from '@EmailMakerReact/lib/email-maker-publish-library';
import {doesEmailContainPremiumContent} from '@EmailMakerReact/lib/email-maker-save-payload-library';
import {ExportItemState, ExportMenuOption, EXPORT_STATE_RESET_DELAY_MS, GETTY_BUY_DESIGN_PATH} from './export-menu.types';
import type {ExportStateMap} from './export-menu.types';
import {ExportMenuItem} from './components/export-menu-item/export-menu-item';
import type {ExportMenuItemTitles} from './components/export-menu-item/export-menu-item.types';
import styles from './export-menu.module.scss';

const DEFAULT_EXPORT_STATES: ExportStateMap = {
  [ExportMenuOption.COPY_HTML]: ExportItemState.DEFAULT,
  [ExportMenuOption.COPY_AS_TEXT]: ExportItemState.DEFAULT,
  [ExportMenuOption.DOWNLOAD_HTML]: ExportItemState.DEFAULT,
};

/**
 * Each row independently cycles DEFAULT → PROCESSING → SUCCESS → DEFAULT; the dropdown is forced
 * open through PROCESSING and SUCCESS so the user sees the result before it closes.
 */
export function ExportMenu(): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const [exportStates, setExportStates] = useState<ExportStateMap>(DEFAULT_EXPORT_STATES);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const resetTimers = useRef<Partial<Record<ExportMenuOption, ReturnType<typeof setTimeout>>>>({});

  useEffect((): (() => void) => {
    return (): void => {
      const timers = resetTimers.current;
      for (const key of Object.keys(timers) as ExportMenuOption[]) {
        const timer = timers[key];
        if (timer !== undefined) {
          clearTimeout(timer);
        }
      }
    };
  }, []);

  function trackGA4SectionsUsed(): void {
    const idEmailCampaign = editor ? getCurrentIdEmailCampaign(editor) : null;
    if (idEmailCampaign) {
      trackGA4NumberOfSectionsInEmailCampaign(idEmailCampaign);
    }
  }

  function setOptionState(option: ExportMenuOption, state: ExportItemState): void {
    setExportStates((prev) => ({...prev, [option]: state}));
  }

  function scheduleReset(option: ExportMenuOption): void {
    const existing = resetTimers.current[option];
    if (existing !== undefined) {
      clearTimeout(existing);
    }
    resetTimers.current[option] = setTimeout((): void => {
      setOptionState(option, ExportItemState.DEFAULT);
      setIsDropdownOpen(false);
    }, EXPORT_STATE_RESET_DELAY_MS);
  }

  function onClickCopy(): void {
    if (!editor) {
      return;
    }
    authenticateUser(editor, {
      cb: (): void => {
        trackGA4SectionsUsed();
        setOptionState(ExportMenuOption.COPY_HTML, ExportItemState.PROCESSING);
        void copyEmailHtmlForExport(editor)
          .then((): void => {
            recordEmailExport(editor, EmailMakerExportOption.COPY_EMAIL);
            setOptionState(ExportMenuOption.COPY_HTML, ExportItemState.SUCCESS);
            scheduleReset(ExportMenuOption.COPY_HTML);
          })
          .catch((err: unknown): void => {
            window.PMW.log('Export copy failed', err);
            setOptionState(ExportMenuOption.COPY_HTML, ExportItemState.DEFAULT);
            setIsDropdownOpen(false);
          });
      },
    });
  }

  function onClickCopyHtml(): void {
    if (!editor) {
      return;
    }
    authenticateUser(editor, {
      cb: (): void => {
        trackGA4SectionsUsed();
        setOptionState(ExportMenuOption.COPY_AS_TEXT, ExportItemState.PROCESSING);
        void copyEmailHtmlAsTextForExport(editor)
          .then((): void => {
            recordEmailExport(editor, EmailMakerExportOption.COPY_HTML);
            setOptionState(ExportMenuOption.COPY_AS_TEXT, ExportItemState.SUCCESS);
            scheduleReset(ExportMenuOption.COPY_AS_TEXT);
          })
          .catch((err: unknown): void => {
            window.PMW.log('Export copy HTML failed', err);
            setOptionState(ExportMenuOption.COPY_AS_TEXT, ExportItemState.DEFAULT);
            setIsDropdownOpen(false);
          });
      },
    });
  }

  function onClickDownload(): void {
    if (!editor) {
      return;
    }
    authenticateUser(editor, {
      cb: (): void => {
        trackGA4SectionsUsed();
        setOptionState(ExportMenuOption.DOWNLOAD_HTML, ExportItemState.PROCESSING);
        void downloadHtmlForMarketingEmail(editor)
          .then((): void => {
            recordEmailExport(editor, EmailMakerExportOption.DOWNLOAD_HTML);
            setOptionState(ExportMenuOption.DOWNLOAD_HTML, ExportItemState.SUCCESS);
            scheduleReset(ExportMenuOption.DOWNLOAD_HTML);
          })
          .catch((err: unknown): void => {
            window.PMW.log('Export download failed', err);
            setOptionState(ExportMenuOption.DOWNLOAD_HTML, ExportItemState.DEFAULT);
            setIsDropdownOpen(false);
          });
      },
    });
  }

  const hasGettyContent = editor ? doesEmailContainPremiumContent(editor) : false;

  function renderPopup(): ReactElement {
    return (
      <>
        <ul className={styles.list}>
          <li>
            <ExportMenuItem
              defaultIcon="icon-copy"
              titles={getCopyTitles()}
              description={window.i18next.t('pmwjs_export_copy_description')}
              state={exportStates[ExportMenuOption.COPY_HTML]}
              onClick={onClickCopy}
            />
          </li>
          <li>
            <ExportMenuItem
              defaultIcon="icon-pencil-line"
              titles={getCopyHtmlTitles()}
              description={window.i18next.t('pmwjs_export_copy_html_description')}
              state={exportStates[ExportMenuOption.COPY_AS_TEXT]}
              onClick={onClickCopyHtml}
            />
          </li>
          <li>
            <ExportMenuItem
              defaultIcon="icon-cloud-download"
              titles={getDownloadHtmlTitles()}
              description={window.i18next.t('pmwjs_export_download_html_description')}
              state={exportStates[ExportMenuOption.DOWNLOAD_HTML]}
              onClick={onClickDownload}
            />
          </li>
        </ul>
        {renderGettyLabel(hasGettyContent)}
      </>
    );
  }

  return (
    <Dropdown
      selector={renderSelector()}
      popup={renderPopup()}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popUpHasCustomWidth
      popupClassName={styles.popup}
      isControlled
      open={isDropdownOpen}
      onOpen={(): void => {
        setIsDropdownOpen(true);
      }}
      onClose={(): void => {
        const anyProcessingOrSuccess = Object.values(exportStates).some((s) => s === ExportItemState.PROCESSING || s === ExportItemState.SUCCESS);
        if (!anyProcessingOrSuccess) {
          setIsDropdownOpen(false);
        }
      }}
      doCloseOnInnerClick={false}
    />
  );
}

function renderGettyLabel(hasGettyContent: boolean): ReactElement | null {
  if (!hasGettyContent) {
    return null;
  }

  return (
    <div className={styles.gettyLabel}>
      <Icon icon="icon-info" shape={IconShape.SQUARE} type={IconType.GHOST} size={IconSize.SIZE_ICON_16} />
      <span className={styles.gettyLabelText}>
        {window.i18next.t('pmwjs_getty_stock')}{' '}
        <a href={window.PMW.util.site_url(GETTY_BUY_DESIGN_PATH)} rel="noopener noreferrer">
          {window.i18next.t('pmwjs_remove_watermark')}
        </a>
      </span>
    </div>
  );
}

function renderSelector(): ReactElement {
  return <Button type={Type.DARK_SECONDARY} size={Size.SMALL} text={window.i18next.t('pmwjs_export')} icon="icon-sign-in" iconPlacement={IconPlacement.LEFT} />;
}

function getCopyTitles(): ExportMenuItemTitles {
  return {
    default: window.i18next.t('pmwjs_copy'),
    processing: window.i18next.t('pmwjs_export_copying'),
    success: window.i18next.t('pmwjs_export_copied'),
  };
}

function getCopyHtmlTitles(): ExportMenuItemTitles {
  return {
    default: window.i18next.t('pmwjs_copy_html'),
    processing: window.i18next.t('pmwjs_export_copying_html'),
    success: window.i18next.t('pmwjs_export_html_copied'),
  };
}

function getDownloadHtmlTitles(): ExportMenuItemTitles {
  return {
    default: window.i18next.t('pmwjs_export_download_html'),
    processing: window.i18next.t('pmwjs_export_downloading_html'),
    success: window.i18next.t('pmwjs_export_html_downloaded'),
  };
}
